import { useState } from "react";
import { Link } from "react-router";

import { useQuery } from "@tanstack/react-query";
import {
  ArrowUpRight,
  FileJson,
  Loader2,
  RotateCcw,
  ShieldAlert,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import {
  enrichmentEntityHref,
  enrichmentEntityLabel,
  isEnrichmentEntity,
  isParkLimitReached,
  readEnrichmentJobRef,
  readEnrichmentParkState,
  readEnrichmentResult,
} from "@/lib/tina/enrichment-jobs";
import { useEnrichmentIntake } from "@/pages/tina/hooks/use-enrichment-intake";
import { StageArtifactsDialog } from "@/pages/tina/items/components/stage-timeline";
import { useItemsRepository } from "@/pages/tina/items/hooks/use-items-repository";
import type { PipelineItem, StageExecutionRead } from "@/types/tina";

/**
 * The park-limit state, which is a job that is *not* in HITL.
 *
 * The third low-confidence verdict closes the job `done` /
 * `final_outcome="manual_review"` with
 * `metadata.enrichment_result.park_limit_reached = true`. Such a row lands in
 * `DonePanel`, and without this notice it looks like a job that succeeded —
 * while the entity is still `NeedsReview` with nothing on its canonical fields,
 * and nothing anywhere else in IMS points at it.
 *
 * **Gated on the flag, never on `final_outcome === "manual_review"` alone.**
 * Before the HITL gate existed that outcome was stamped on every gated
 * enrichment, so the looser test would grow a scary banner on every old row.
 *
 * Renders nothing for every other item, so `DonePanel` can mount it
 * unconditionally. The gate is deliberately a **hook-free** wrapper: the body
 * below reaches for the tina data provider, and a scrape item's DonePanel must
 * neither pay for that nor be forced to stub it in tests.
 */
export function ParkLimitNotice({ item }: { item: PipelineItem }) {
  if (!isParkLimitReached(item)) return null;
  return <ParkLimitNoticeBody item={item} />;
}

function ParkLimitNoticeBody({ item }: { item: PipelineItem }) {
  const [artifactsFor, setArtifactsFor] = useState<StageExecutionRead | null>(
    null,
  );
  const { getStages } = useItemsRepository();
  const { enqueue } = useEnrichmentIntake();
  const [queueing, setQueueing] = useState(false);

  const result = readEnrichmentResult(item);
  const jobRef = readEnrichmentJobRef(item);
  const parks = readEnrichmentParkState(item);

  // The proposal the curator wants is the one the *third* verdict produced, and
  // that lives in the last `enrich` execution's artifacts. (Not
  // `GET /hitl/{id}/proposal`, which does not exist — and which would have
  // served park #2's proposal anyway.)
  const stagesQuery = useQuery({
    queryKey: ["tina-items", item.id, "enrich-stages"],
    queryFn: () => getStages(item.id),
    staleTime: 5 * 60 * 1000,
  });

  const entityType = result?.entity_type ?? jobRef?.entity_type ?? null;
  const entityId = result?.entity_id ?? jobRef?.entity_id ?? null;
  const entityLabel = enrichmentEntityLabel(entityType);
  const entityHref = enrichmentEntityHref(entityType, entityId);

  const lastEnrich = (stagesQuery.data ?? [])
    .filter((execution) => execution.stage === "enrich")
    .sort((a, b) => b.attempt - a.attempt)[0];

  // Why "View last proposal" is unavailable, when it is — the button used to
  // sit permanently disabled and say nothing, so a failed `GET /events/{id}/
  // stages` and a job that genuinely ran no `enrich` stage looked identical,
  // and both looked like a UI that had simply stopped working. Only reached
  // once the query has settled: mid-load the spinner is the explanation.
  const proposalBlocker = stagesQuery.isError
    ? `Could not load this job's stages, so the last proposal cannot be opened: ${toHumanErrorMessage(stagesQuery.error)}`
    : !stagesQuery.isLoading && !lastEnrich
      ? "This job has no recorded enrich stage, so there is no proposal artifact to open."
      : null;

  async function reEnrich() {
    // `entityType` arrives as a loose string from the stage result / job ref;
    // the guard narrows it to the union `enqueue` requires rather than casting.
    if (!isEnrichmentEntity(entityType) || !entityId) return;
    setQueueing(true);
    try {
      // A fresh job row starts at `park_count = 0`, which is the escape hatch
      // the park cap deliberately leaves open — and the only non-SQL way out of
      // a park-limited entity.
      await enqueue({ entity_type: entityType, entity_id: entityId });
      await stagesQuery.refetch();
    } catch {
      /* useEnrichmentIntake already notified + captured */
    } finally {
      setQueueing(false);
    }
  }

  return (
    <div className="flex flex-col gap-3 rounded-md border border-amber-500/40 bg-amber-500/10 p-3">
      <div className="flex items-start gap-2">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
        <div className="text-sm">
          <p className="font-medium">
            Enrichment gave up after {parks.count ?? parks.limit} attempts.
          </p>
          <p className="text-muted-foreground">
            {entityLabel}{" "}
            {entityId ? (
              <code className="font-mono text-xs">{entityId}</code>
            ) : (
              "this entity"
            )}{" "}
            is still <strong>NeedsReview</strong> — the latest proposal was
            written back, but nothing was promoted to its canonical fields.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {entityHref && (
          <Button asChild size="sm" variant="outline">
            <Link to={entityHref}>
              Open entity
              <ArrowUpRight className="ml-1 h-3.5 w-3.5" />
            </Link>
          </Button>
        )}
        <Button
          size="sm"
          variant="outline"
          disabled={
            stagesQuery.isFetching || (!lastEnrich && !stagesQuery.isError)
          }
          onClick={() => {
            // A failed fetch turns the button into its own retry rather than
            // leaving the curator with a dead control and a page reload as the
            // only recourse.
            if (stagesQuery.isError) {
              void stagesQuery.refetch();
              return;
            }
            setArtifactsFor(lastEnrich ?? null);
          }}
        >
          {stagesQuery.isFetching ? (
            <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
          ) : (
            <FileJson className="mr-1 h-3.5 w-3.5" />
          )}
          {stagesQuery.isError
            ? "Retry loading proposal"
            : "View last proposal"}
        </Button>
        <Button
          size="sm"
          disabled={queueing || !entityType || !entityId}
          onClick={() => void reEnrich()}
        >
          {queueing ? (
            <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
          ) : (
            <RotateCcw className="mr-1 h-3.5 w-3.5" />
          )}
          Re-enrich
        </Button>
      </div>

      {proposalBlocker && (
        <p
          className={
            stagesQuery.isError
              ? "text-xs text-destructive"
              : "text-xs text-muted-foreground"
          }
        >
          {proposalBlocker}
        </p>
      )}

      <StageArtifactsDialog
        execution={artifactsFor}
        onOpenChange={(open) => {
          if (!open) setArtifactsFor(null);
        }}
      />
    </div>
  );
}
